import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useNavigate } from 'react-router';
import { useAuth } from '@/providers/AuthProvider';
import { Spinner } from '@/components/ui/spinner';
import { ILoginForm } from '@/types';

export const LoginForm = () => {
  const navigate = useNavigate();
  const { user, handleLogin, authLoading } = useAuth();
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm<ILoginForm>();

  if (user && user.role) {
    navigate(`/${user.role.toLowerCase()}/dashboard`, { replace: true });
  }

  const onSubmit: SubmitHandler<ILoginForm> = async (data) => {
    try {
      await handleLogin(data);

      if (authLoading) {
        return <Spinner />;
      }

      if (user && user.role) {
        navigate(`/${user?.role?.toLowerCase() || '/login'}`, {
          replace: true,
        });
      }
    } catch (err) {
      console.error(err);
      toast('Failed login', {
        dismissible: true,
      });
    }
  };

  return (
    <Tabs
      defaultValue="email"
      className="w-full bg-card p-6 rounded-lg shadow-lg"
    >
      <TabsContent value="email" className="space-y-4">
        <div className="text-center mb-6">
          <h2 className="text-3xl font-bold">
            <span className="text-primary">Field</span>
            <span className="text-secondary">Ops</span>
          </h2>
          <p className="text-gray-500">Sign in to your account</p>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="domain">Organization</Label>
            <Input
              {...register('domain', {
                required: 'Enter organization name',
              })}
              id="domain"
              type="text"
            />
            {errors.domain && (
              <p className="text-sm font-medium text-red-500">
                {errors.domain.message}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              {...register('email', { required: 'Enter email' })}
              id="email"
              type="email"
              placeholder="name@example.com"
            />
            {errors.email && (
              <p className="text-sm font-medium text-red-500">
                {errors.email.message}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              {...register('password', { required: 'Enter password' })}
              id="password"
              type="password"
            />
            {errors.password && (
              <p className="text-sm font-medium text-red-500">
                {errors.password.message}
              </p>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? 'Signing in...' : 'Log In'}
          </Button>
        </form>

        {/* Legal Disclaimers/Links */}
        <div className="text-center text-xs text-muted-foreground mt-6">
          By continuing, you agree to our{' '}
          <a href="/privacy" className="hover:text-gray-600">
            Privacy Policy
          </a>
          <span className="mx-1.5">•</span>
          <a href="/terms" className="hover:text-gray-600">
            Terms of Service
          </a>
        </div>
      </TabsContent>
    </Tabs>
  );
};
